// План → Нагрузка: подходы и тоннаж по группам мышц за неделю против прошлых 8 недель; рядом Garmin.

import { useState } from 'react'

import { Card, ErrorText, IconButton, PageSpinner, Stat } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDate, formatDayMonth, formatNumber, formatSigned } from '@/lib/format'
import { addDays, todayIso } from '@/lib/periods'
import { useMuscleLabels } from '@/pages/workouts/api'

import { useVolume } from './api'
import { TrainingStatusLine } from './garmin'
import { mondayOf, volumeRows } from './logic'

export function LoadPage() {
  const [week, setWeek] = useState(mondayOf(todayIso()))
  const volume = useVolume(week)
  const labelOf = useMuscleLabels()

  const nav = (
    <div className="mb-3 flex items-center gap-1">
      <IconButton icon="chevron-left" label="Прошлая неделя" onClick={() => setWeek(addDays(week, -7))} />
      <h2 className="min-w-0 flex-1 truncate text-center text-base font-semibold tabular-nums text-fg">
        {formatDayMonth(week)} – {formatDate(addDays(week, 6))}
      </h2>
      <IconButton
        icon="chevron-right"
        label="Следующая неделя"
        disabled={week >= mondayOf(todayIso())}
        onClick={() => setWeek(addDays(week, 7))}
      />
    </div>
  )
  if (volume.isPending) return <>{nav}<PageSpinner /></>
  if (volume.error) return <>{nav}<ErrorText error={volume.error} /></>

  const { current, previous, average_sets } = volume.data
  const rows = volumeRows(current.muscles, average_sets, labelOf)
  const history = [...previous, current]
  const maxSets = Math.max(1, ...history.map((w) => w.sets))
  const avgTotal = previous.length ? previous.reduce((s, w) => s + w.sets, 0) / previous.length : null

  return (
    <div className="min-w-0 space-y-4">
      {nav}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card>
          <Stat
            label="Подходы"
            value={formatNumber(current.sets, { digits: 0 })}
            hint={avgTotal !== null ? `в среднем ${formatNumber(avgTotal)} за ${previous.length} нед.` : undefined}
          />
        </Card>
        <Card>
          <Stat label="Тоннаж" value={formatNumber(current.tonnage_kg, { digits: 0 })} unit="кг" />
        </Card>
        <Card>
          <Stat label="Тренировок" value={current.sessions} />
        </Card>
        <Card>
          <Stat
            label="Нагрузка Garmin"
            value={current.training_load !== null ? formatNumber(current.training_load, { digits: 0 }) : '—'}
            hint={current.training_load !== null ? 'острая нагрузка Garmin, отдельно от объёма' : 'нет данных из Garmin'}
          />
          <TrainingStatusLine />
        </Card>
      </div>

      <Card title="Подходы по неделям" subtitle="Рабочие подходы без разминочных">
        <div className="flex h-28 items-end gap-1" role="list" aria-label="Подходы по неделям">
          {history.map((w) => (
            <div key={w.week_start} role="listitem" className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <span className="text-[10px] tabular-nums text-muted">{w.sets || ''}</span>
              <div
                className={cn('w-full rounded-t', w.week_start === current.week_start ? 'bg-accent' : 'bg-surface-2')}
                style={{ height: `${Math.max(2, (w.sets / maxSets) * 80)}px` }}
                title={`${formatDayMonth(w.week_start)}: ${w.sets}`}
              />
              <span className="truncate text-[10px] tabular-nums text-muted">{formatDayMonth(w.week_start)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card
        title="По группам мышц"
        subtitle="Подходы за неделю и в среднем за 8 недель; вспомогательная группа — половина подхода и тоннажа"
        flush
      >
        {rows.length ? (
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted">
                <th className="px-4 py-2 font-medium">Группа</th>
                <th className="w-20 px-1 py-2 text-right font-medium">Подходы</th>
                <th className="w-20 py-2 pl-1 pr-4 text-right font-medium sm:pr-1">Среднее</th>
                <th className="hidden w-24 px-4 py-2 text-right font-medium sm:table-cell">Тоннаж</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.muscle} className="border-b border-border last:border-0">
                  <td className="truncate px-4 py-2 text-fg">{r.label}</td>
                  <td className="px-1 py-2 text-right tabular-nums text-fg">
                    {formatNumber(r.sets)}
                    {r.average ? (
                      <span
                        className={cn('ml-1 text-xs', r.delta > 0 ? 'text-accent' : r.delta < 0 ? 'text-warning' : 'text-muted')}
                      >
                        {formatSigned(r.delta)}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 pl-1 pr-4 text-right tabular-nums text-muted sm:pr-1">{formatNumber(r.average)}</td>
                  <td className="hidden px-4 py-2 text-right tabular-nums text-muted sm:table-cell">
                    {formatNumber(r.tonnage, { digits: 0 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-4 pb-4 text-sm text-muted">За эту и прошлые недели силовых не было.</p>
        )}
      </Card>
    </div>
  )
}
